import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, getCompanyId } from '../firebase';
import Navbar from '../components/Navbar';

export default function Timesheets() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [preview, setPreview] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editClockIn, setEditClockIn] = useState('');
  const [editClockOut, setEditClockOut] = useState('');

  useEffect(() => {
    const loadEmployees = async () => {
      const companyId = await getCompanyId();
      const snapshot = await getDocs(query(collection(db, 'users'), where('companyId', '==', companyId)));
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const emps = data.filter(u => u.role === 'employee');
      setEmployees(emps);
      setSelectedEmployees(emps.map(e => e.id));
      setLoading(false);
    };
    loadEmployees();
  }, []);

  const toggleEmployee = (id) => {
    if (selectedEmployees.includes(id)) {
      setSelectedEmployees(selectedEmployees.filter(e => e !== id));
    } else {
      setSelectedEmployees([...selectedEmployees, id]);
    }
  };

  const toggleAll = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(e => e.id));
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '--';
    const date = new Date(seconds * 1000);
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours < 12 ? 'AM' : 'PM';
    const displayHour = hours % 12 === 0 ? 12 : hours % 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getHours = (entry) => {
    if (!entry.clockIn || !entry.clockOut) return null;
    const diff = entry.clockOut.seconds - entry.clockIn.seconds;
    return (diff / 3600).toFixed(2);
  };

  const getBreakDuration = (entry) => {
    if (!entry.breaks || entry.breaks.length === 0) return null;
    const totalBreakSeconds = entry.breaks.reduce((sum, b) => {
      if (!b.breakIn || !b.breakOut) return sum;
      return sum + (b.breakOut.seconds - b.breakIn.seconds);
    }, 0);
    return totalBreakSeconds > 0 ? (totalBreakSeconds / 3600).toFixed(2) : null;
  };

  const getNetHours = (entry) => {
    const total = getHours(entry);
    if (!total) return null;
    const breakHours = getBreakDuration(entry) || 0;
    return (parseFloat(total) - parseFloat(breakHours)).toFixed(2);
  };

  const formatLocation = (loc) => {
    if (!loc) return 'N/A';
    return `${loc.latitude.toFixed(4)}, ${loc.longitude.toFixed(4)}`;
  };
  const toDatetimeLocal = (seconds) => {
    if (!seconds) return '';
    const date = new Date(seconds * 1000);
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  const handleDeleteEntry = async (entryId) => {
    if (window.confirm('Delete this time entry?')) {
      await deleteDoc(doc(db, 'timeEntries', entryId));
      await loadPreview();
    }
  };

  const handleSaveEntry = async (entryId) => {
    const clockInDate = editClockIn ? new Date(editClockIn) : new Date(editingEntry.clockIn.seconds * 1000);
    const clockOutDate = editClockOut ? new Date(editClockOut) : (editingEntry.clockOut ? new Date(editingEntry.clockOut.seconds * 1000) : null);

    if (clockOutDate && clockOutDate <= clockInDate) {
      alert('Clock out time must be after clock in time!');
      return;
    }

    const updates = {};
    if (editClockIn) updates.clockIn = clockInDate;
    if (editClockOut) updates.clockOut = clockOutDate;

    await updateDoc(doc(db, 'timeEntries', entryId), updates);
    setEditingEntry(null);
    setEditClockIn('');
    setEditClockOut('');
    await loadPreview();
  };

  const loadPreview = async () => {
    const [fromYear, fromMonth, fromDay] = fromDate.split('-').map(Number);
    const from = new Date(fromYear, fromMonth - 1, fromDay, 0, 0, 0, 0);
    const [toYear, toMonth, toDay] = toDate.split('-').map(Number);
    const to = new Date(toYear, toMonth - 1, toDay, 23, 59, 59, 999);

    const results = [];
    for (const empId of selectedEmployees) {
      const emp = employees.find(e => e.id === empId);
      if (!emp) continue;

      const companyId = await getCompanyId();
      const snapshot = await getDocs(query(collection(db, 'timeEntries'), where('userId', '==', empId), where('companyId', '==', companyId)));
      const entries = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(e => {
          if (!e.clockIn) return false;
          const entryDate = new Date(e.clockIn.seconds * 1000);
          return entryDate >= from && entryDate <= to;
        })
        .sort((a, b) => a.clockIn?.seconds - b.clockIn?.seconds);

      results.push({ emp, entries });
    }
    setPreview(results);
  };

  const handleExport = async () => {
    if (selectedEmployees.length === 0) {
      alert('Please select at least one employee');
      return;
    }
    setExporting(true);

    const [fromYear, fromMonth, fromDay] = fromDate.split('-').map(Number);
    const from = new Date(fromYear, fromMonth - 1, fromDay, 0, 0, 0, 0);
    const [toYear, toMonth, toDay] = toDate.split('-').map(Number);
    const to = new Date(toYear, toMonth - 1, toDay, 23, 59, 59, 999);

    let csv = `OnShifts Timesheet Export\n`;
    csv += `Period: ${from.toLocaleDateString()} - ${to.toLocaleDateString()}\n`;
    csv += `Generated: ${new Date().toLocaleDateString()}\n\n`;

    for (const empId of selectedEmployees) {
      const emp = employees.find(e => e.id === empId);
      if (!emp) continue;

      const companyId = await getCompanyId();
      const snapshot = await getDocs(query(collection(db, 'timeEntries'), where('userId', '==', empId), where('companyId', '==', companyId)));
      const entries = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(e => {
          if (!e.clockIn) return false;
          const entryDate = new Date(e.clockIn.seconds * 1000);
          return entryDate >= from && entryDate <= to;
        })
        .sort((a, b) => a.clockIn?.seconds - b.clockIn?.seconds);

      csv += `Employee: ${emp.name}\nEmail: ${emp.email}\n`;
      csv += `Date,Clock In,Clock Out,Total Hours,Break Hours,Net Hours,Location,Status,Late Reason,Early Reason,Break Details\n`;

      let totalHours = 0;
      let lateCount = 0;
      let earlyInCount = 0;
      let earlyCount = 0;
      let overtimeHours = 0;

      entries.forEach(entry => {
        const hours = getHours(entry) || '--';
        const breakHours = getBreakDuration(entry) || '--';
        const netHours = getNetHours(entry) || '--';
        if (hours !== '--') totalHours += parseFloat(netHours !== '--' ? netHours : hours);
        if (entry.status === 'late') lateCount++;
        if (entry.status === 'early') earlyInCount++;
        if (entry.earlyClockOut) earlyCount++;
        if (entry.overtime) overtimeHours += entry.overtimeHours || 0;

        const breakDetails = (entry.breaks || []).map((b, i) =>
          `Break ${i + 1}: ${formatTime(b.breakIn?.seconds)} - ${formatTime(b.breakOut?.seconds)}`
        ).join(' | ');

        csv += `"${entry.date || '--'}","${formatTime(entry.clockIn?.seconds)}","${formatTime(entry.clockOut?.seconds)}","${hours}","${breakHours}","${netHours}","${formatLocation(entry.clockInLocation)}","${entry.status || 'on-time'}","${entry.lateReason || ''}","${entry.earlyClockOutReason || entry.earlyClockInReason || ''}","${breakDetails}"\n`;
      });

      csv += `\nSummary\nTotal Hours,${totalHours.toFixed(2)}\nLate Count,${lateCount}\nEarly Clock Ins,${earlyInCount}\nEarly Clock Outs,${earlyCount}\nOvertime Hours,${overtimeHours.toFixed(2)}\nTotal Shifts,${entries.length}\n\n\n`;
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `OnShift_${fromDate}_to_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExporting(false);
  };

  return (
    <div style={styles.page}>
      <Navbar />
      <div style={styles.container}>
        <h1 style={styles.title}>Timesheets</h1>

        <div style={styles.controls}>
          <div style={styles.dateSection}>
            <div style={styles.dateGroup}>
              <label style={styles.label}>From</label>
              <input
                type="date"
                style={styles.dateInput}
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
              />
            </div>
            <div style={styles.dateArrow}>→</div>
            <div style={styles.dateGroup}>
              <label style={styles.label}>To</label>
              <input
                type="date"
                style={styles.dateInput}
                value={toDate}
                onChange={e => setToDate(e.target.value)}
              />
            </div>
          </div>

          <div style={styles.actions}>
            <button style={styles.previewBtn} onClick={loadPreview}>
              Preview
            </button>
            <button style={styles.exportBtn} onClick={handleExport} disabled={exporting}>
              {exporting ? 'Exporting...' : '📤 Export CSV'}
            </button>
          </div>
        </div>

        <div style={styles.employeeSection}>
          <div style={styles.employeeHeader}>
            <span style={styles.label}>Employees</span>
            <button style={styles.toggleAll} onClick={toggleAll}>
              {selectedEmployees.length === employees.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div style={styles.employeeList}>
            {employees.map(emp => (
              <div
                key={emp.id}
                style={{
                  ...styles.employeeChip,
                  ...(selectedEmployees.includes(emp.id) ? styles.employeeChipActive : {})
                }}
                onClick={() => toggleEmployee(emp.id)}
              >
                {emp.name}
              </div>
            ))}
          </div>
        </div>

        {preview.length > 0 && (
          <div style={styles.preview}>
            {preview.map(({ emp, entries }) => {
              const totalHours = entries.reduce((sum, e) => {
                const h = getHours(e);
                return sum + (h ? parseFloat(h) : 0);
              }, 0);
              const lateCount = entries.filter(e => e.status === 'late').length;

              return (
                <div key={emp.id} style={styles.previewCard}>
                  <div style={styles.previewHeader}>
                    <div>
                      <div style={styles.previewName}>{emp.name}</div>
                      <div style={styles.previewEmail}>{emp.email}</div>
                    </div>
                    <div style={styles.previewStats}>
                      <span style={styles.previewStat}>{totalHours.toFixed(1)}h total</span>
                      {lateCount > 0 && <span style={{ ...styles.previewStat, color: '#f87171' }}>{lateCount} late</span>}
                      <span style={styles.previewStat}>{entries.length} shifts</span>
                    </div>
                  </div>

                  {entries.length === 0 ? (
                    <div style={styles.noEntries}>No entries in this period</div>
                  ) : (
                    <table style={styles.table}>
                      <thead>
                        <tr>
                            {['Date', 'Clock In', 'Clock Out', 'Hours', 'Break', 'Net Hours', 'Status', 'Actions'].map(h => (
                            <th key={h} style={styles.th}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map(entry => (
                          <React.Fragment key={entry.id}>
                            <tr style={styles.tr}>
                              <td style={styles.td}>{entry.date}</td>
                              <td style={styles.td}>{formatTime(entry.clockIn?.seconds)}</td>
                              <td style={styles.td}>{formatTime(entry.clockOut?.seconds)}</td>
                              <td style={styles.td}>{getHours(entry) ? `${getHours(entry)}h` : 'Active'}</td>
                              <td style={{ ...styles.td, color: '#fb923c' }}>
                                {getBreakDuration(entry) ? `${getBreakDuration(entry)}h` : '--'}
                              </td>
                              <td style={{ ...styles.td, color: '#4ade80' }}>
                                {getNetHours(entry) ? `${getNetHours(entry)}h` : 'Active'}
                              </td>
                              <td style={styles.td}>
                                <span style={{
                                  ...styles.badge,
                                  backgroundColor: entry.status === 'late' ? '#2d1414' : entry.status === 'early' ? '#431407' : '#14532d',
                                  color: entry.status === 'late' ? '#f87171' : entry.status === 'early' ? '#fb923c' : '#4ade80',
                                }}>
                                  {entry.status || 'on-time'}
                                </span>
                              </td>
                              <td style={styles.td}>
                                <div style={styles.entryActions}>
                                  <button
                                    style={styles.editEntryBtn}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setEditClockIn('');
                                      setEditClockOut('');
                                      setEditingEntry(editingEntry?.id === entry.id ? null : entry);
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    style={styles.deleteEntryBtn}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDeleteEntry(entry.id);
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {editingEntry?.id === entry.id && (
                              <tr style={styles.tr}>
                                <td colSpan={6} style={styles.td}>
                                  <div style={styles.editRow}>
                                    <div style={styles.editGroup}>
                                      <label style={styles.editLabel}>Clock In</label>
                                      <input
                                        type="datetime-local"
                                        style={styles.editInput}
                                        defaultValue={toDatetimeLocal(entry.clockIn?.seconds)}
                                        onChange={e => setEditClockIn(e.target.value)}
                                      />
                                    </div>
                                    <div style={styles.editGroup}>
                                      <label style={styles.editLabel}>Clock Out</label>
                                      <input
                                        type="datetime-local"
                                        style={styles.editInput}
                                        defaultValue={toDatetimeLocal(entry.clockOut?.seconds)}
                                        onChange={e => setEditClockOut(e.target.value)}
                                      />
                                    </div>
                                    <button
                                      style={styles.saveEntryBtn}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleSaveEntry(entry.id);
                                      }}
                                    >
                                      Save
                                    </button>
                                    <button
                                      style={styles.cancelEntryBtn}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setEditingEntry(null);
                                        setEditClockIn('');
                                        setEditClockOut('');
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0f0f0f',
  },
  container: {
    padding: '32px 40px',
    maxWidth: 1200,
    margin: '0 auto',
  },
  title: {
    color: '#fff',
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 24,
    marginTop: 0,
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 16,
  },
  dateSection: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 12,
  },
  dateGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    color: '#999',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  dateInput: {
    backgroundColor: '#2a2a2a',
    border: 'none',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#fff',
    fontSize: 14,
    outline: 'none',
    colorScheme: 'dark',
  },
  dateArrow: {
    color: '#999',
    fontSize: 20,
    paddingBottom: 8,
  },
  actions: {
    display: 'flex',
    gap: 10,
  },
  previewBtn: {
    backgroundColor: '#2a2a2a',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  exportBtn: {
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  employeeSection: {
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
  },
  employeeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  toggleAll: {
    backgroundColor: 'transparent',
    color: '#4f46e5',
    border: 'none',
    cursor: 'pointer',
    fontSize: 13,
  },
  employeeList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  employeeChip: {
    backgroundColor: '#2a2a2a',
    color: '#999',
    padding: '8px 16px',
    borderRadius: 20,
    cursor: 'pointer',
    fontSize: 14,
    border: '1px solid transparent',
  },
  employeeChipActive: {
    backgroundColor: '#1a1a3e',
    color: '#fff',
    border: '1px solid #4f46e5',
  },
  preview: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  previewCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    overflow: 'hidden',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #2a2a2a',
  },
  previewName: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  previewEmail: {
    color: '#999',
    fontSize: 13,
    marginTop: 2,
  },
  previewStats: {
    display: 'flex',
    gap: 16,
  },
  entryActions: {
  display: 'flex',
  gap: 6
},

editEntryBtn: {
  backgroundColor: '#2a2a2a',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '4px 10px',
  cursor: 'pointer',
  fontSize: 12
},

deleteEntryBtn: {
  backgroundColor: '#2d1414',
  color: '#f87171',
  border: 'none',
  borderRadius: 6,
  padding: '4px 10px',
  cursor: 'pointer',
  fontSize: 12
},

editRow: {
  display: 'flex',
  alignItems: 'flex-end',
  gap: 12,
  padding: '12px 0',
  backgroundColor: '#2a2a2a',
  borderRadius: 8,
  padding: 16
},

editGroup: {
  display: 'flex',
  flexDirection: 'column',
  gap: 6
},

editLabel: {
  color: '#999',
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 1
},

editInput: {
  backgroundColor: '#3a3a3a',
  border: 'none',
  borderRadius: 8,
  padding: '8px 12px',
  color: '#fff',
  fontSize: 13,
  outline: 'none',
  colorScheme: 'dark'
},

saveEntryBtn: {
  backgroundColor: '#4f46e5',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '8px 16px',
  cursor: 'pointer',
  fontWeight: 'bold',
  alignSelf: 'flex-end'
},

cancelEntryBtn: {
  backgroundColor: '#2a2a2a',
  color: '#999',
  border: 'none',
  borderRadius: 8,
  padding: '8px 16px',
  cursor: 'pointer',
  alignSelf: 'flex-end'
},
  previewStat: {
    color: '#4ade80',
    fontSize: 14,
    fontWeight: 'bold',
  },
  noEntries: {
    color: '#999',
    padding: 20,
    textAlign: 'center',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '12px 20px',
    textAlign: 'left',
    color: '#999',
    fontSize: 12,
    fontWeight: '600',
    borderBottom: '1px solid #2a2a2a',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tr: {
    borderBottom: '1px solid #2a2a2a',
  },
  td: {
    padding: '12px 20px',
    color: '#fff',
    fontSize: 14,
  },
  badge: {
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 'bold',
  },
};